import { Button } from '@/components/ui/button'
import { Phone, Navigation, Copy } from 'lucide-react'
import { navigationService } from '@/services/NavigationService'
import { toast } from 'sonner'

interface PlaceInfoContentProps {
  attributes: Record<string, unknown>
}

const FIELD_LABELS: Record<string, string> = {
  ten: 'Tên',
  diachi: 'Địa chỉ',
  chinhanh: 'Chi nhánh',
  phamvigiaiquyet: 'Phạm vi giải quyết',
  duongdaynong: 'Đường dây nóng',
  xa: 'Xã/Phường',
  quan: 'Quận/Huyện',
  tinh: 'Tỉnh/Thành phố',
  sdt: 'Số điện thoại',
  email: 'Email',
  website: 'Website',
  ghichu: 'Ghi chú',
  mota: 'Mô tả',
  loai: 'Loại',
  trangthai: 'Trạng thái',
  ngaytao: 'Ngày tạo',
  ngaysua: 'Ngày sửa',
}

const PHONE_FIELDS = ['duongdaynong', 'sdt']
const COPY_FIELDS = ['diachi']

// const EXCLUDED_FIELDS = ['objectid', 'shape', 'geometry', 'globalid', 'toadox', 'toadoy']

export function PlaceInfoContent({ attributes }: PlaceInfoContentProps) {
  const handleCall = (phoneNumber: string) => {
    window.location.href = `tel:${phoneNumber}`
  }

  const handleDirections = (address: string) => {
    // Check if toadox and toadoy are available
    const toadox = attributes.toadox as string | undefined
    const toadoy = attributes.toadoy as string | undefined

    let destination: string

    if (toadox && toadoy) {
      // Use coordinates if available (they are in DMS format)
      // Combine toadox (longitude) and toadoy (latitude) into a single string
      // NavigationService will parse the DMS format automatically
      destination = `${toadoy}+${toadox}`
    } else {
      // Fall back to address
      destination = address
    }

    // Build Google Maps URL for directions
    const url = navigationService.buildGoogleMapsUrl({
      destination: destination,
      travelMode: 'driving',
    })
    navigationService.openNavigation(url)
  }

  const handleCopyAddress = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address)
      toast.success(`${address} đã được copy`, {
        id: 'copy-address',
        position: 'top-right',
        duration: 3000,
      })
    } catch (error) {
      console.error('Failed to copy address:', error)
      toast.error('Không thể copy địa chỉ', {
        id: 'copy-address-error',
      })
    }
  }

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return ''
    if (typeof value === 'string') return value
    if (typeof value === 'number') return value.toLocaleString('vi-VN')
    return String(value)
  }

  const displayFields = Object.entries(attributes)
    // .filter(([key]) => !EXCLUDED_FIELDS.includes(key.toLowerCase()))
    .filter(([key]) => Object.keys(FIELD_LABELS).includes(key.toLowerCase()))
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => ({
      key,
      label: FIELD_LABELS[key.toLowerCase()] || key,
      value: formatValue(value as string | number),
    }))

  const addressField = displayFields.find((field) => field.key.toLowerCase() === 'diachi')

  return (
    <div className="space-y-3">
      {displayFields.map((field) => (
        <div key={field.key} className="space-y-1">
          <p className="text-sm font-medium text-gray-500">{field.label}</p>
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-gray-900">{field.value}</p>
            {COPY_FIELDS.includes(field.key.toLowerCase()) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => handleCopyAddress(field.value)}
              >
                <Copy className="h-3 w-3" />
              </Button>
            )}
            {(PHONE_FIELDS.includes(field.key.toLowerCase())) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCall(field.value)}
                className="h-6 w-6 p-0 transition-colors hover:text-gray-600"
              >
                <Phone className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ))}

      <div className="flex flex-col gap-2 pt-2">
        {addressField && (
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-center"
            onClick={() => handleDirections(addressField.value)}
          >
            <Navigation className="mr-2 h-4 w-4" />
            Chỉ đường
          </Button>
        )}
      </div>
    </div>
  )
}
